import React, { useState, useCallback, useMemo } from "react";
import { Input, Button, List, Tag, message, Divider, Select, Space } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

/** Common FlatBuffer scalar types */
const FBS_SCALAR_TYPES = [
  "bool",
  "byte",
  "ubyte",
  "short",
  "ushort",
  "int",
  "uint",
  "float",
  "long",
  "ulong",
  "double",
  "int8",
  "uint8",
  "int16",
  "uint16",
  "int32",
  "uint32",
  "int64",
  "uint64",
  "float32",
  "float64",
  "string",
];

/** User-defined type discovered from schema files */
export interface SchemaTypeName {
  name: string;
  kind: "enum" | "table" | "union" | "struct";
  namespace: string;
}

interface FbsTableInfo {
  name: string;
  namespace: string;
  fields: string[];
}

interface TableEditorProps {
  tableNode: FbsTableInfo;
  /** User-defined types from all loaded schema files */
  schemaTypes?: SchemaTypeName[];
  onAddField: (fieldName: string, fieldType: string) => void;
}

function validateFieldName(name: string): string | null {
  if (!name || name.trim().length === 0) return "Field name cannot be empty";
  if (!/^[a-z][a-z0-9_]*$/i.test(name))
    return "Field name must start with a letter and contain only letters, digits, and underscores";
  return null;
}

function TableEditor({ tableNode, schemaTypes = [], onAddField }: TableEditorProps) {
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("int32");
  const [isVector, setIsVector] = useState(false);

  /** Build grouped options: Scalars, then Enums, Tables, Unions, Structs */
  const typeOptions = useMemo(() => {
    const groups: {
      label: string;
      options: { label: React.ReactNode; value: string }[];
    }[] = [
      {
        label: "Scalar Types",
        options: FBS_SCALAR_TYPES.map((t) => ({ label: t, value: t })),
      },
    ];

    const byKind: Record<string, SchemaTypeName[]> = {};
    schemaTypes.forEach((st) => {
      if (!byKind[st.kind]) byKind[st.kind] = [];
      byKind[st.kind].push(st);
    });

    const kindLabels: Record<string, { label: string; color: string }> = {
      enum: { label: "Enums", color: "blue" },
      table: { label: "Tables", color: "green" },
      union: { label: "Unions", color: "purple" },
      struct: { label: "Structs", color: "orange" },
    };

    ["enum", "table", "union", "struct"].forEach((kind) => {
      const items = byKind[kind];
      if (items && items.length > 0) {
        const meta = kindLabels[kind];
        groups.push({
          label: meta.label,
          options: items
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((st) => ({
              label: (
                <span>
                  {st.namespace ? `${st.namespace}.` : ""}
                  {st.name}{" "}
                  <Tag color={meta.color} style={{ fontSize: 9, lineHeight: "14px" }}>
                    {kind}
                  </Tag>
                </span>
              ),
              value: st.namespace ? `${st.namespace}.${st.name}` : st.name,
            })),
        });
      }
    });

    return groups;
  }, [schemaTypes]);

  const handleAdd = useCallback(() => {
    const trimmed = fieldName.trim();
    if (!trimmed) return;

    const nameError = validateFieldName(trimmed);
    if (nameError) {
      message.error(nameError);
      return;
    }

    const existingNames = tableNode.fields.map((f) => f.split(":")[0]?.trim() ?? "");
    if (existingNames.includes(trimmed)) {
      message.error(`Field "${trimmed}" already exists in this table`);
      return;
    }

    if (!fieldType.trim()) {
      message.error("Field type is required");
      return;
    }

    const finalType = isVector ? `[${fieldType.trim()}]` : fieldType.trim();
    onAddField(trimmed, finalType);
    setFieldName("");
    message.success(`Added field "${trimmed}: ${finalType}" to ${tableNode.name}`);
  }, [fieldName, fieldType, isVector, tableNode, onAddField]);

  return (
    <div>
      <Divider orientation="left">
        Table: {tableNode.namespace}::{tableNode.name} -- {tableNode.fields.length} fields
      </Divider>

      <List
        size="small"
        bordered
        dataSource={tableNode.fields}
        renderItem={(field, index) => (
          <List.Item>
            <span>
              <Tag color="blue">{index}</Tag>
              {field}
            </span>
          </List.Item>
        )}
        style={{ maxWidth: 600, marginBottom: 12 }}
      />

      <Space wrap style={{ maxWidth: 600 }}>
        <Input
          placeholder="field_name (snake_case)"
          value={fieldName}
          onChange={(e) => setFieldName(e.target.value)}
          size="small"
          style={{ width: 200 }}
          onPressEnter={handleAdd}
        />
        <Select
          value={fieldType}
          onChange={setFieldType}
          size="small"
          style={{ width: 240 }}
          showSearch
          optionFilterProp="value"
          options={typeOptions}
        />
        <Button
          size="small"
          type={isVector ? "primary" : "default"}
          onClick={() => setIsVector((v) => !v)}
        >
          [ ] Vector
        </Button>
        <Button
          type="primary"
          size="small"
          icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />}
          onClick={handleAdd}
          disabled={!fieldName.trim()}
        >
          Add Field
        </Button>
      </Space>
    </div>
  );
}

export default TableEditor;
